/**
 * Fixes CommonMark-invalid emphasis: closing `**` / `*` must not be separated from
 * the inner text by whitespace (otherwise markdown-it and similar parsers show literal asterisks).
 */
export function normalizeMarkdownEmphasis(markdown: string): string {
  if (!markdown || !markdown.trim()) return markdown;
  let normalized = markdown.replace(/\*\*\s*([^*]+?)\s*\*\*/g, (_, c) => `**${c.trim()}**`);
  normalized = normalized.replace(/(?<!\*)\*\s*([^*]+?)\s*\*(?!\*)/g, (_, c) => `*${c.trim()}*`);
  return normalized;
}

/**
 * Converts simple markdown (**bold**, *italic*, newlines) to HTML for display.
 */
export function markdownToHtml(markdown: string): string {
  if (!markdown || !markdown.trim()) return '';

  const normalized = normalizeMarkdownEmphasis(markdown);

  const escaped = normalized
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

  // **bold** first, then *italic*
  return escaped
    .replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*]+?)\*/g, '<em>$1</em>')
    // Drop any leftover runs of asterisks that didn't form valid emphasis
    // (e.g. orphaned `**`/`****` from empty marks) so they aren't shown literally.
    .replace(/\*{2,}/g, '')
    .replace(/\n/g, '<br />');
}

/** Placeholders so block merges do not collapse intentional blank lines */
const HTML_EMPTY_BLOCK = '\uE000';
const MARKDOWN_PARAGRAPH_GAP = '\uE001';

const BLOCK_TAGS = new Set(['DIV', 'P']);

/** True when the element makes its text bold via a semantic tag or an inline font-weight. */
function isBoldElement(el: HTMLElement): boolean {
  const tag = el.tagName.toUpperCase();
  if (tag === 'B' || tag === 'STRONG') return true;
  const fw = el.style?.fontWeight;
  if (!fw) return false;
  if (fw === 'bold' || fw === 'bolder') return true;
  const numeric = parseInt(fw, 10);
  return !isNaN(numeric) && numeric >= 600;
}

/** True when the element makes its text italic via a semantic tag or an inline font-style. */
function isItalicElement(el: HTMLElement): boolean {
  const tag = el.tagName.toUpperCase();
  if (tag === 'I' || tag === 'EM') return true;
  return el.style?.fontStyle === 'italic';
}

type InlineRun = { br: true } | { br?: false; text: string; bold: boolean; italic: boolean };

/**
 * Converts HTML from contenteditable back to simple markdown by walking the DOM.
 * Robust to how browsers emit formatting \u2014 semantic tags (<b>/<i>) OR inline styles
 * (font-weight/font-style) on spans or block elements \u2014 which a regex pass misses.
 */
function htmlToMarkdownViaDom(html: string): string {
  const container = document.createElement('div');
  container.innerHTML = html;

  const runs: InlineRun[] = [];
  const lastIsBreak = () => runs.length > 0 && (runs[runs.length - 1] as { br?: boolean }).br === true;

  const walk = (node: Node, bold: boolean, italic: boolean) => {
    node.childNodes.forEach(child => {
      if (child.nodeType === Node.TEXT_NODE) {
        const text = child.textContent;
        if (text) runs.push({ text, bold, italic });
        return;
      }
      if (child.nodeType !== Node.ELEMENT_NODE) return;

      const el = child as HTMLElement;
      const tag = el.tagName.toUpperCase();
      if (tag === 'BR') {
        runs.push({ br: true });
        return;
      }
      // A new block element starts on its own line.
      if (BLOCK_TAGS.has(tag) && runs.length && !lastIsBreak()) {
        runs.push({ br: true });
      }
      walk(el, bold || isBoldElement(el), italic || isItalicElement(el));
    });
  };

  walk(container, false, false);

  // Merge adjacent text runs sharing the same formatting so split text nodes don't
  // produce stray `**a****b**`.
  const merged: InlineRun[] = [];
  for (const run of runs) {
    const prev = merged[merged.length - 1] as { br?: boolean; text?: string; bold?: boolean; italic?: boolean };
    if (
      !run.br &&
      prev &&
      !prev.br &&
      prev.bold === run.bold &&
      prev.italic === run.italic
    ) {
      prev.text = (prev.text ?? '') + run.text;
    } else {
      merged.push({ ...run });
    }
  }

  let md = '';
  for (const run of merged) {
    if (run.br) {
      md += '\n';
      continue;
    }
    const text = run.text.replace(/\u00A0/g, ' ');
    if (run.bold || run.italic) {
      const lead = text.match(/^\s*/)?.[0] ?? '';
      const trail = text.match(/\s*$/)?.[0] ?? '';
      const core = text.trim();
      // Skip whitespace-only formatted nodes \u2014 never emit empty `**`/`*`.
      if (core) {
        let wrapped = core;
        if (run.italic) wrapped = `*${wrapped}*`;
        if (run.bold) wrapped = `**${wrapped}**`;
        md += `${lead}${wrapped}${trail}`;
      } else {
        md += text;
      }
    } else {
      md += text;
    }
  }

  return normalizeMarkdownEmphasis(md.trim());
}

/**
 * Converts HTML from contenteditable back to simple markdown.
 * Uses the DOM when available (browser) and falls back to a regex pass otherwise.
 */
export function htmlToMarkdown(html: string): string {
  if (!html || !html.trim()) return '';

  if (typeof document !== 'undefined' && typeof document.createElement === 'function') {
    return htmlToMarkdownViaDom(html);
  }

  // Wrap inner text in markers, but skip empty marks so we never emit `**`/`*` with nothing inside.
  const emphasis = (open: string, close: string) => (_match: string, content: string) => {
    const trimmed = content.trim();
    return trimmed ? `${open}${trimmed}${close}` : '';
  };

  const raw = html
    // Empty line as its own block (Enter in contenteditable) — must become \n\n in markdown
    .replace(/<div>\s*<br\s*\/?>\s*<\/div>/gi, HTML_EMPTY_BLOCK)
    .replace(/<p>\s*<br\s*\/?>\s*<\/p>/gi, HTML_EMPTY_BLOCK)
    // Merge "</div> … empty … <div>" into one token before generic </div><div> → \n
    .replace(new RegExp(`</div>${HTML_EMPTY_BLOCK}<div>`, 'g'), MARKDOWN_PARAGRAPH_GAP)
    // Sibling blocks (single line break). Do not use \\s* here — it would eat \\n\\n and remove blank lines.
    .replace(/<\/div><div>/gi, '\n')
    .replace(/<\/p><p>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    // Some browsers apply formatting via CSS spans (styleWithCSS) instead of <b>/<i> —
    // map those to markers too so bold/italic survive the round-trip.
    .replace(/<span[^>]*font-weight:\s*(?:bold|[5-9]00)[^>]*>(.*?)<\/span>/gi, emphasis('**', '**'))
    .replace(/<span[^>]*font-style:\s*italic[^>]*>(.*?)<\/span>/gi, emphasis('*', '*'))
    .replace(/<strong>(.*?)<\/strong>/gi, emphasis('**', '**'))
    .replace(/<b>(.*?)<\/b>/gi, emphasis('**', '**'))
    .replace(/<em>(.*?)<\/em>/gi, emphasis('*', '*'))
    .replace(/<i>(.*?)<\/i>/gi, emphasis('*', '*'))
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(new RegExp(MARKDOWN_PARAGRAPH_GAP, 'g'), '\n\n')
    // Stray empty blocks (e.g. trailing blank line) not caught above
    .replace(new RegExp(HTML_EMPTY_BLOCK, 'g'), '\n\n')
    .trim();

  return normalizeMarkdownEmphasis(raw);
}
